import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SNACKBAR_DURATION } from '@/core/constants/app';

import { AddToast, SnackbarContext } from './Snackbar';

type Props = {
	children: React.ReactNode;
};

/**
 * Renders a toast messages provider.
 *
 * It uses React Context to provide the `addToast` function.
 *
 * The `useAddToast` hook is exposed and can be used to access the `addToast` function anywhere in the app,
 * to trigger a new toast message.
 */
export const SnackbarProvider = ({ children }: Props) => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { t } = useTranslation();

	const handleAction = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(snackbarId: any, action: any) => {
			if (!action) {
				return undefined;
			}

			return (
				<Button
					aria-label={t('Snakbar action button')}
					size='small'
					onClick={() => {
						action.onClick();
						closeSnackbar(snackbarId);
					}}
					color='inherit'
				>
					{action.label}
				</Button>
			);
		},
		[closeSnackbar, t]
	);

	const handleAddToast: AddToast = useCallback(
		(message, action, severity = 'info', persist = false) => {
			enqueueSnackbar(message, {
				persist,
				variant: severity,
				action: snackbarId => handleAction(snackbarId, action),
				autoHideDuration: SNACKBAR_DURATION
			});
		},
		[enqueueSnackbar, handleAction]
	);

	return (
		<SnackbarContext.Provider value={handleAddToast}>
			{children}
		</SnackbarContext.Provider>
	);
};
